import React, { useEffect, useMemo, useState } from 'react';
import { CardBody, Badge } from 'reactstrap';
import {
  formatAlphaPrice,
  coalesceAlphaPrice,
  resolveRowCapable,
} from '../../../../../../../utils/deliveryDetailUtils';
import {
  formatParcelSize,
  formatIncotermRoute,
  hasCounterOffer,
  isCounterValueChanged,
  isCounterDateChanged,
  buildLpgOfferTableModel,
  cellClass,
  headerCellClass,
  renderLpgPortCell,
} from '../../../../../../../../../shared/offer-domain/delivery';
import { COUNTER_ACCENT } from '../../../../../../../../../shared/offer-domain/constants';
import { getCargoRatioKey } from '../../../../../../../../TenderOpening/components/Detail/HpsParcel/lpg/utils';

const LPG_UNIT = 'MT';
const ALPHA_UNIT = 'USD/MT';

const getTableColSpan = requiresCargoRatio => {
  let cols = 8; // expand, incoterm, port1-3, parcel, capable, alpha
  if (requiresCargoRatio) cols += 1;
  return cols;
};

const getRowKey = data =>
  data?.quantityInformationDetailId ??
  `${data?.id ?? ''}-${data?.quantityIncotermId ?? ''}-${data?.cargoRatio ?? ''}-${data?.parcelSize ?? data?.deliveryBasis ?? ''}`;

const DeliveryDetailTableLpg = ({ items = [], requiresCargoRatio = false }) => {
  const tableRows = useMemo(
    () => buildLpgOfferTableModel(items, LPG_UNIT),
    [items]
  );
  const colSpan = getTableColSpan(requiresCargoRatio);
  const [expandedRows, setExpandedRows] = useState({});

  const counterByGroupId = useMemo(() => {
    const map = {};
    tableRows.forEach(row => {
      map[row.groupId] = map[row.groupId] || hasCounterOffer(row.data);
    });
    return map;
  }, [tableRows]);

  useEffect(() => {
    const initial = {};
    tableRows.forEach(row => {
      if (row.showGroupCells && counterByGroupId[row.groupId]) {
        initial[row.groupId] = true;
      }
    });
    if (!Object.keys(initial).length) return;
    setExpandedRows(prev => {
      const merged = { ...prev };
      Object.keys(initial).forEach(key => {
        if (!(key in merged)) merged[key] = true;
      });
      return merged;
    });
  }, [tableRows, counterByGroupId]);

  const toggleRowExpand = groupId => {
    setExpandedRows(prev => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const renderCounterOfferRow = (data, groupMeta) => {
    const incotermRoute = formatIncotermRoute(
      data.counterIncoterm,
      data.counterRoute
    );
    const originalIncotermRoute = formatIncotermRoute(
      data.incoterm,
      data.route
    );
    const incotermRouteChanged =
      !!data.counterIncoterm &&
      isCounterValueChanged(originalIncotermRoute, incotermRoute);

    const parcelByKey = new Map();
    (items || []).forEach(item => {
      if (
        String(item.quantityIncotermId ?? '') !==
        String(data.quantityIncotermId ?? '')
      ) {
        return;
      }
      const parcelKey = String(
        item.parcelSizeLabel ?? item.parcelSize ?? item.deliveryBasis ?? ''
      );
      if (!parcelKey) return;
      const current = parcelByKey.get(parcelKey);
      if (
        !current ||
        (!current.quantityInformationDetailCounterId &&
          item.quantityInformationDetailCounterId)
      ) {
        parcelByKey.set(parcelKey, item);
      }
    });

    const masterParcelOrder = groupMeta?.parcelSizes ?? [];
    const parcelItems = [];
    if (masterParcelOrder.length) {
      const usedKeys = new Set();
      masterParcelOrder.forEach(label => {
        const key = String(label);
        if (parcelByKey.has(key)) {
          parcelItems.push(parcelByKey.get(key));
          usedKeys.add(key);
          return;
        }
        for (const [parcelKey, item] of parcelByKey.entries()) {
          if (usedKeys.has(parcelKey)) continue;
          const itemLabel = String(
            item.parcelSizeLabel ??
              formatParcelSize(item.parcelSize ?? item.deliveryBasis, LPG_UNIT)
          );
          if (itemLabel === key) {
            parcelItems.push(item);
            usedKeys.add(parcelKey);
            break;
          }
        }
      });
      parcelByKey.forEach((item, key) => {
        if (!usedKeys.has(key)) parcelItems.push(item);
      });
    } else {
      parcelByKey.forEach(item => parcelItems.push(item));
    }

    if (!parcelItems.length) {
      if (masterParcelOrder.length) {
        masterParcelOrder.forEach(label => {
          parcelItems.push({ ...data, parcelSizeLabel: label });
        });
      } else {
        parcelItems.push(data);
      }
    }

    const counterRatiosFromGroup = (items || []).find(
      item =>
        String(item.quantityIncotermId ?? '') ===
          String(data.quantityIncotermId ?? '') &&
        Array.isArray(item.counterCargoRatios) &&
        item.counterCargoRatios.length
    )?.counterCargoRatios;

    const counterRatios =
      Array.isArray(data.counterCargoRatios) && data.counterCargoRatios.length
        ? data.counterCargoRatios
        : Array.isArray(counterRatiosFromGroup) && counterRatiosFromGroup.length
          ? counterRatiosFromGroup
          : [{ id: null, label: null }];

    const counterRows = [];
    counterRatios.forEach((ratio, ratioIndex) => {
      parcelItems.forEach((parcelItem, parcelIndex) => {
        const ratioKey = getCargoRatioKey(ratio?.id);
        const parcelRatio =
          (parcelItem.counterCargoRatios || []).find(
            entry =>
              String(entry.quantityInformationCargoRatioFK ?? '') ===
                String(parcelItem.quantityInformationCargoRatioId ?? '') ||
              getCargoRatioKey(entry.id) === ratioKey
          ) || null;
        const parcelAlpha = coalesceAlphaPrice(
          parcelRatio?.alpha,
          parcelItem.counterAlpha,
          parcelItem.alpha2,
          parcelItem.alpha,
          parcelItem.alphaMitra1
        );
        const mergedRatio = parcelRatio
          ? {
              ...ratio,
              ...parcelRatio,
              id: ratio?.id ?? parcelRatio.id,
              label: ratio?.label ?? parcelRatio.label,
              alpha: parcelAlpha,
            }
          : {
              ...ratio,
              alpha: parcelAlpha,
            };
        counterRows.push({
          ratio: mergedRatio,
          parcelItem,
          showGroupCells: ratioIndex === 0 && parcelIndex === 0,
          showRatioCell: requiresCargoRatio && parcelIndex === 0,
          groupRowSpan: counterRatios.length * parcelItems.length,
          ratioRowSpan: parcelItems.length,
          parcelSizes: groupMeta?.parcelSizes || [],
        });
      });
    });

    return (
      <tr className='bg-light'>
        <td colSpan={colSpan} className='p-0 border-0'>
          <div
            className='bg-light-subtle'
            style={{
              borderLeft: `3px solid ${COUNTER_ACCENT}`,
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <div
              className='px-2 py-2'
              style={{ width: '100%', overflowX: 'auto' }}
            >
              <table
                className='table table-sm table-borderless mb-0 align-middle w-100'
                style={{ tableLayout: 'fixed', width: '100%', minWidth: 0 }}
              >
                <thead>
                  <tr className='text-muted border-bottom'>
                    <th
                      className='fs-12 fw-semibold ps-1 pb-2'
                      style={{ width: '9%' }}
                    />
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: requiresCargoRatio ? '16%' : '18%' }}
                    >
                      Incoterm - Route
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: requiresCargoRatio ? '14%' : '16%' }}
                    >
                      Port 1
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: requiresCargoRatio ? '10%' : '12%' }}
                    >
                      Port 2
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: requiresCargoRatio ? '8%' : '10%' }}
                    >
                      Port 3
                    </th>
                    {requiresCargoRatio && (
                      <th
                        className='fs-12 fw-semibold pb-2 pe-2'
                        style={{ width: '10%' }}
                      >
                        Cargo Ratio
                      </th>
                    )}
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: requiresCargoRatio ? '10%' : '12%' }}
                    >
                      Parcel Size
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: requiresCargoRatio ? '12%' : '14%' }}
                    >
                      Alpha
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {counterRows.map((row, rowIndex) => (
                    <tr key={`counter-${row.ratio?.id ?? 'x'}-${rowIndex}`}>
                      {row.showGroupCells && (
                        <td
                          rowSpan={row.groupRowSpan}
                          className='ps-1 py-2 align-top'
                        >
                          <span
                            className='badge rounded-pill px-2 py-1 fw-semibold'
                            style={{
                              backgroundColor: COUNTER_ACCENT,
                              color: '#fff',
                              fontSize: '11px',
                            }}
                          >
                            Counter
                          </span>
                        </td>
                      )}
                      {row.showGroupCells && (
                        <td
                          rowSpan={row.groupRowSpan}
                          className={`fs-12 py-2 pe-2 align-top text-break ${incotermRouteChanged ? 'fw-semibold' : 'fw-medium'}`}
                          style={{
                            color: incotermRouteChanged
                              ? COUNTER_ACCENT
                              : undefined,
                            wordBreak: 'break-word',
                          }}
                        >
                          {incotermRoute || '-'}
                        </td>
                      )}
                      {row.showGroupCells && (
                        <td
                          rowSpan={row.groupRowSpan}
                          className={`${cellClass} align-top`}
                          style={{ minWidth: 0 }}
                        >
                          {renderLpgPortCell(
                            data.counterLoadingPort,
                            groupMeta?.parcelSizes ?? [],
                            {
                              startDate: data.counterStartDate,
                              endDate: data.counterEndDate,
                              fallbackDate: data.counterAddAld,
                              highlight: {
                                port: isCounterValueChanged(
                                  data.port1 || data.loadingPort,
                                  data.counterLoadingPort
                                ),
                                parcelSize: false,
                                date: isCounterDateChanged(
                                  data.port1StartDate,
                                  data.port1EndDate,
                                  data.counterStartDate,
                                  data.counterEndDate,
                                  data.addAld,
                                  data.counterAddAld
                                ),
                              },
                            }
                          )}
                        </td>
                      )}
                      {row.showGroupCells && (
                        <td rowSpan={row.groupRowSpan} className={cellClass} />
                      )}
                      {row.showGroupCells && (
                        <td rowSpan={row.groupRowSpan} className={cellClass} />
                      )}
                      {row.showRatioCell && (
                        <td
                          rowSpan={row.ratioRowSpan}
                          className={`${cellClass} fs-13 align-top`}
                        >
                          {row.ratio?.label ? (
                            <span
                              className='badge bg-danger-subtle text-danger border border-danger-subtle fw-semibold rounded-3'
                              style={{ fontSize: '12px' }}
                            >
                              {row.ratio.label}
                            </span>
                          ) : (
                            <span className='text-muted'>-</span>
                          )}
                        </td>
                      )}
                      <td
                        className={`${cellClass} fs-13 fw-semibold align-top`}
                      >
                        {row.parcelItem.parcelSizeLabel ||
                          formatParcelSize(
                            row.parcelItem.parcelSize ??
                              row.parcelItem.deliveryBasis,
                            LPG_UNIT
                          )}
                      </td>
                      <td
                        className={`${cellClass} fs-13 align-top`}
                        style={{ minWidth: 0 }}
                      >
                        {formatAlphaPrice(row.ratio?.alpha ?? '', ALPHA_UNIT)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  if (!items.length) {
    return (
      <CardBody className='p-3'>
        <div className='text-center py-4 text-muted fs-13'>
          No delivery detail available.
        </div>
      </CardBody>
    );
  }

  return (
    <CardBody className='p-3'>
      <div className='table-responsive table-card border rounded-3'>
        <table
          className='table table-hover table-centered align-middle mb-0'
          style={{ minWidth: '1180px' }}
        >
          <thead className='table-light'>
            <tr>
              <th className={headerCellClass} style={{ width: '44px' }} />
              <th className={headerCellClass} style={{ minWidth: '130px' }}>
                Incoterm - Route
              </th>
              <th className={headerCellClass} style={{ minWidth: '200px' }}>
                Port 1
              </th>
              <th className={headerCellClass} style={{ minWidth: '160px' }}>
                Port 2
              </th>
              <th className={headerCellClass} style={{ minWidth: '120px' }}>
                Port 3
              </th>
              {requiresCargoRatio && (
                <th className={headerCellClass} style={{ minWidth: '110px' }}>
                  Cargo Ratio
                </th>
              )}
              <th className={headerCellClass} style={{ minWidth: '110px' }}>
                Parcel Size
              </th>
              <th
                className={`${headerCellClass} text-center`}
                style={{ minWidth: '100px' }}
              >
                Is Capable?
              </th>
              <th className={headerCellClass} style={{ minWidth: '150px' }}>
                Alpha
              </th>
            </tr>
          </thead>
          <tbody>
            {tableRows.length > 0 ? (
              tableRows.map(row => {
                const { data, groupId, groupMeta } = row;
                const rowKey = getRowKey(data);
                const isCapable = resolveRowCapable(data, true);
                const isExpanded = !!expandedRows[groupId];
                const groupHasCounter = !!counterByGroupId[groupId];

                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      className={
                        isExpanded && row.showGroupCells && groupHasCounter
                          ? 'border-bottom-1'
                          : undefined
                      }
                      style={
                        isExpanded && groupHasCounter
                          ? { borderLeft: `3px solid ${COUNTER_ACCENT}` }
                          : undefined
                      }
                    >
                      {row.showGroupCells && (
                        <td rowSpan={row.groupRowSpan} className={cellClass}>
                          {groupHasCounter ? (
                            <button
                              type='button'
                              className='btn btn-link btn-sm p-0 text-muted'
                              onClick={() => toggleRowExpand(groupId)}
                            >
                              <i
                                className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line fs-16`}
                              />
                            </button>
                          ) : null}
                        </td>
                      )}

                      {row.showGroupCells && (
                        <td
                          rowSpan={row.groupRowSpan}
                          className={`${cellClass} fs-13`}
                        >
                          {groupMeta.incotermRoute}
                        </td>
                      )}

                      {row.showGroupCells && (
                        <td rowSpan={row.groupRowSpan} className={cellClass}>
                          {renderLpgPortCell(
                            groupMeta.port1,
                            groupMeta.parcelSizes,
                            {
                              portLabel: 'Location',
                              startDate: groupMeta.port1StartDate,
                              endDate: groupMeta.port1EndDate,
                              fallbackDate: groupMeta.addAld,
                              locationNames: groupMeta.port1Names,
                              route: groupMeta.route,
                            }
                          )}
                        </td>
                      )}

                      {row.showGroupCells && (
                        <td rowSpan={row.groupRowSpan} className={cellClass}>
                          {renderLpgPortCell(
                            groupMeta.port2,
                            groupMeta.port2 ? groupMeta.parcelSizes : [],
                            {
                              startDate: groupMeta.port2StartDate,
                              endDate: groupMeta.port2EndDate,
                              fallbackDate: groupMeta.addAld2,
                            }
                          )}
                        </td>
                      )}

                      {row.showGroupCells && (
                        <td rowSpan={row.groupRowSpan} className={cellClass}>
                          {renderLpgPortCell(
                            groupMeta.port3,
                            groupMeta.port3 ? groupMeta.parcelSizes : [],
                            {
                              startDate: groupMeta.port3StartDate,
                              endDate: groupMeta.port3EndDate,
                              fallbackDate: groupMeta.addAld3,
                            }
                          )}
                        </td>
                      )}

                      {requiresCargoRatio && row.showCargoRatioCell && (
                        <td
                          rowSpan={row.cargoRatioRowSpan}
                          className={`${cellClass} fs-13`}
                        >
                          {row.cargoRatioLabel ? (
                            <span
                              className='badge bg-primary-subtle text-primary border border-primary-subtle fw-semibold rounded-3'
                              style={{ fontSize: '12px' }}
                            >
                              {row.cargoRatioLabel}
                            </span>
                          ) : (
                            <span className='text-muted'>-</span>
                          )}
                        </td>
                      )}

                      <td className={`${cellClass} fs-13 fw-semibold`}>
                        {data.parcelSizeLabel ||
                          formatParcelSize(
                            data.parcelSize ?? data.deliveryBasis,
                            LPG_UNIT
                          )}
                      </td>

                      <td className={`${cellClass} text-center`}>
                        {isCapable ? (
                          <Badge
                            color='primary'
                            className='px-2 py-1 fs-12 fw-medium rounded-pill'
                          >
                            Compliant
                          </Badge>
                        ) : groupHasCounter ? (
                          <span
                            className='badge rounded-pill px-2 py-1 fs-12 fw-medium'
                            style={{
                              backgroundColor: COUNTER_ACCENT,
                              color: '#fff',
                            }}
                          >
                            Countered
                          </span>
                        ) : (
                          <Badge
                            color='secondary'
                            className='px-2 py-1 fs-12 fw-medium rounded-pill'
                          >
                            Not Offered
                          </Badge>
                        )}
                      </td>

                      <td className={`${cellClass} fs-13`}>
                        {formatAlphaPrice(data.alpha || '', ALPHA_UNIT)}
                      </td>
                    </tr>

                    {row.isLastInGroup &&
                      isExpanded &&
                      groupHasCounter &&
                      renderCounterOfferRow(
                        groupMeta.representative || data,
                        groupMeta
                      )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={colSpan} className='text-center text-muted py-4'>
                  No delivery detail available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </CardBody>
  );
};

export default DeliveryDetailTableLpg;
